import {includesQuery} from 'app/core/helpers/string';
import {CheckboxTreeNodes, CheckboxTreePreset} from 'app/shared/components/checkbox-tree/checkbox-tree.interface';
import {CheckboxState} from '../checkbox/checkbox.component';
import {CheckboxTreeItem} from './checkbox-tree-item';
import {CheckboxTreeNode} from './checkbox-tree.interface';

export class CheckboxTree {
    selected: Set<any>;
    items: Array<CheckboxTreeItem>;
    nodeItemMap = new Map<CheckboxTreeNode, CheckboxTreeItem>();
    valueItemMap = new Map<any, CheckboxTreeItem>();
    private childrenState = new Map<CheckboxTreeItem, boolean>();
    private checkboxStates = new Map<CheckboxTreeItem, CheckboxState>();
    filteredChildren = new Map<CheckboxTreeItem, Array<CheckboxTreeItem>>();
    private filterQuery: string;

    constructor(
        private nodes: CheckboxTreeNodes,
        selected: Set<any>
    ) {
        this.items = this.createItems(nodes, null);
        this.selected = selected;
    }

    changeSelected(selected: Set<any>) {
        this.selected = selected;
        this.clearCheckBoxStates();
    }

    private createItems(nodes: CheckboxTreeNodes, parent: CheckboxTreeItem): Array<CheckboxTreeItem> {
        return nodes.map(node => {
            let item = new CheckboxTreeItem(node, parent);
            if (Array.isArray(node.children) && node.children.length) {
                item.setChildren(this.createItems(node.children, item));
            }
            this.nodeItemMap.set(node, item);
            this.valueItemMap.set(item.getValue(), item);
            return item;
        });
    }

    toggleNode(node: CheckboxTreeItem, checked: boolean) {
        this.clearBranchState(this.findRoot(node));
        if (checked) {
            this.checkOption(node);
        } else {
            this.uncheckOption(node);
        }
    }

    toggleChildren(node: CheckboxTreeItem, state?: boolean) {
        let newState = state === undefined ? !this.childrenState.get(node) : state;
        this.childrenState.set(node, newState);
    }

    isChildrenExpanded(node: CheckboxTreeItem): boolean {
        return this.childrenState.get(node);
    }

    isNodeChecked(node: CheckboxTreeItem): boolean {
        return this.selected.has(node.getValue());
    }

    getChildrenCount(node: CheckboxTreeItem): number {
        if (node.hasChildren() && this.isChildrenExpanded(node)) {
            let children = this.getNodeChildren(node);
            return children.reduce((count, child: CheckboxTreeItem) => {
                if (this.isChildrenExpanded(child)) {
                    count += this.getChildrenCount(child);
                }
                return count;
            }, children.length);
        }
        return 0;
    }

    getCheckboxState(node: CheckboxTreeItem): CheckboxState {
        if (this.checkboxStates.has(node)) {
            return this.checkboxStates.get(node);
        }
        let state: CheckboxState;
        if (node.hasChildren()) {
            let someIndeterminate = false;
            let allChecked = node.children.reduce((checkedAll, opt) => {
                let {checked, indeterminate} = this.getCheckboxState(opt);
                if (checked || indeterminate) {
                    someIndeterminate = true;
                }
                return checked && checkedAll;
            }, true);
            state = {
                indeterminate: allChecked ? false : someIndeterminate,
                checked: allChecked,
            };
        } else {
            state = {
                indeterminate: false,
                checked: this.isNodeChecked(node),
            };
        }
        this.checkboxStates.set(node, state);
        return state;
    }

    getFilteredItems(filterQuery: string, items = this.items): Array<CheckboxTreeItem> {
        if (filterQuery) {
            if (this.filterQuery !== filterQuery) {
                this.filteredChildren.clear();
            }

            this.showNodeChildren(items);

            this.filterQuery = filterQuery;
            let includesTester = includesQuery(filterQuery);
            let filterFn = children =>
                children.filter(node => {
                    if (node.hasChildren()) {
                        let filteredChildren = this.filteredChildren.get(node);
                        if (!filteredChildren) {
                            filteredChildren = filterFn(node.children);
                            this.filteredChildren.set(node, filteredChildren);
                        }
                        return !!filteredChildren.length;
                    } else {
                        return includesTester.test([node.label, String(node.value), node.searchString]);
                    }
                });

            return filterFn(items);
        } else {
            this.filteredChildren.clear();
            return items;
        }
    }

    showNodeChildren(items: CheckboxTreeItem[]) {
        let countChild = 0;

        if (items.length <= 2) {
            items.map(el => {
                this.toggleChildren(el, this.isChildrenExpanded(el));
                return el;
            });
        }

        items.map(el => {
            if (el.children === undefined) {
                return;
            }

            countChild += el.children.length;

            if (countChild > 10) {
                return;
            }

            this.toggleChildren(el, this.isChildrenExpanded(el));
            return el;
        });
    }

    clearCheckBoxStates() {
        this.checkboxStates.clear();
    }

    toggleAll(state: boolean) {
        this.checkboxStates.clear();
        for (let item of this.items) {
            this.toggleNode(item, state);
        }
    }

    applyPreset(preset: CheckboxTreePreset, state: boolean) {
        for (let value of preset.values.values()) {
            let item = this.valueItemMap.get(value);
            if (item) {
                this.toggleNode(item, state);
            } else {
                // eslint-disable-next-line @typescript-eslint/no-unused-expressions
                state ? this.selected.add(value) : this.selected.delete(value);
            }
        }
    }

    *allChildren(): IterableIterator<CheckboxTreeItem> {
        for (let item of this.nodeItemMap.values()) {
            if (!item.hasChildren()) {
                yield item;
            }
        }
    }

    private getNodeChildren(node: CheckboxTreeItem) {
        if (this.filterQuery) {
            return this.filteredChildren.get(node) || node.children;
        } else {
            return node.children;
        }
    }

    private clearBranchState(node: CheckboxTreeItem) {
        if (!node) {
            return;
        }
        this.checkboxStates.delete(node);
        if (node.hasChildren()) {
            for (let child of node.children) {
                this.clearBranchState(child);
            }
        }
    }

    private findRoot(node: CheckboxTreeItem): CheckboxTreeItem {
        if (node) {
            let parent = node.parent;
            return parent === null ? node : this.findRoot(parent);
        }
        return null;
    }

    private checkOption(node: CheckboxTreeItem) {
        if (!node) {
            return;
        }
        if (node.hasChildren()) {
            for (let child of node.children) {
                this.checkOption(child);
            }
        } else {
            this.selected.add(node.getValue());
        }
    }

    private uncheckOption(node: CheckboxTreeItem) {
        if (!node) {
            return;
        }
        if (node.hasChildren()) {
            for (let child of node.children) {
                this.uncheckOption(child);
            }
        } else {
            this.selected.delete(node.getValue());
        }
    }
}
